/**
 * PersonChips - Shared component for displaying tagged persons
 *
 * Used by:
 * - View page (read-only mode)
 * - Edit page player bar (with edit controls)
 *
 * Two modes:
 * - readOnly: Just displays person chips (for View page)
 * - editable: Shows edit/remove buttons (for Edit page)
 */

import { resolveUrl } from '../../../utils/url';
import styles from './PersonChips.module.css';

/**
 * Build display name from person data
 */
function getDisplayName(person) {
  if (!person) return null;

  // Try display_name first
  if (person.display_name) return person.display_name;

  // Try person_name
  if (person.person_name) return person.person_name;

  // Try building from first/last name
  const firstName = person.first_name || '';
  const lastName = person.last_name || '';
  const fullName = `${firstName} ${lastName}`.trim();
  if (fullName) {
    // Add jersey number if available
    if (person.jersey_number) {
      return `${fullName} #${person.jersey_number}`;
    }
    return fullName;
  }

  // Fallback to ID
  return `#${person.id || person.person_id || '?'}`;
}

/**
 * CSS-cropped face thumbnail from preview image.
 * Shows the face region using bbox + crop dimensions.
 */
function FaceThumb({ person }) {
  const previewUrl = person.preview_url;
  const bbox = person.bbox || [];
  const imgW = person.crop_width;
  const imgH = person.crop_height;

  if (!previewUrl || bbox.length !== 4 || !imgW || !imgH) return null;

  const resolvedUrl = resolveUrl(previewUrl);
  const [x1, y1, x2, y2] = bbox;
  const faceW = x2 - x1;
  const faceH = y2 - y1;
  const faceCenterX = x1 + faceW / 2;
  const faceCenterY = y1 + faceH / 2;

  const thumbSize = 16;
  const padding = 1.3;
  const scale = thumbSize / (Math.max(faceW, faceH) * padding);

  return (
    <span className={styles.faceThumbWrap}>
      <img
        src={resolvedUrl}
        alt=""
        draggable={false}
        style={{
          width: imgW * scale,
          height: imgH * scale,
          position: 'absolute',
          left: (thumbSize / 2) - (faceCenterX * scale),
          top: (thumbSize / 2) - (faceCenterY * scale),
        }}
      />
    </span>
  );
}

/**
 * Single person chip component
 */
function PersonChip({
  person,
  readOnly = true,
  isHovered = false,
  onEdit,
  onRemove,
  onHover,
}) {
  const displayName = getDisplayName(person);
  const personId = person.person_id || person.id;

  const handleMouseEnter = () => onHover?.(personId);
  const handleMouseLeave = () => onHover?.(null);

  if (readOnly) {
    return (
      <span
        className={`${styles.chip} ${isHovered ? styles.chipHovered : ''}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <FaceThumb person={person} />
        <span className={styles.name}>{displayName}</span>
      </span>
    );
  }

  // Editable mode
  return (
    <span
      className={`${styles.chip} ${isHovered ? styles.chipHovered : ''}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <FaceThumb person={person} />
      <button
        className={styles.nameBtn}
        onClick={onEdit}
        title="Edit player assignment"
      >
        {displayName}
      </button>
      <button
        className={styles.removeBtn}
        onClick={onRemove}
        title="Remove player"
      >
        x
      </button>
    </span>
  );
}

/**
 * Container for person chips
 */
export default function PersonChips({
  persons = [],
  readOnly = true,
  emptyMessage = 'No persons tagged',
  hoveredPersonId = null,
  onEdit,
  onRemove,
  onHover,
}) {
  if (persons.length === 0) {
    return <span className={styles.empty}>{emptyMessage}</span>;
  }

  return (
    <div className={styles.container}>
      {persons.map((person, idx) => {
        const personId = person.person_id || person.id;
        return (
          <PersonChip
            key={personId || idx}
            person={person}
            readOnly={readOnly}
            isHovered={hoveredPersonId === personId}
            onEdit={() => onEdit?.(person)}
            onRemove={() => onRemove?.(person)}
            onHover={onHover}
          />
        );
      })}
    </div>
  );
}

// Export helper for name resolution
export { getDisplayName };
